import { faFileExport, faPlus, faColumns, faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery, useLazyQuery } from "@apollo/client";
import { ChangeEvent, useCallback, useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dropdown, Spinner, Modal } from "react-bootstrap"; // Import Dropdown and Modal
import Badge, { BadgeBg } from "components/base/Badge"; // Import Badge from custom components
import CommissionDetailsModal, { ProductCommissionDetails, ShippingMethod, CommissionCalculation } from "components/modals/CommissionDetailsModal"; // Import CommissionDetailsModal
// Existing imports
import PageBreadcrumb from "components/common/PageBreadcrumb";
import FilterTab, { FilterTabItem } from "components/common/FilterTab";
import FilterButtonGroup, { FilterMenu } from "components/common/FilterButtonGroup";
import SearchBox from "components/common/SearchBox";
import Button from "components/base/Button";
import ColumnVisibilityToggle from "components/common/ColumnVisibilityToggle";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/products/useAdvanceTable";

// Table and columns
import ProductsTablecolumns, { productsTablecolumns } from "components/tables/ProductsTable";
// GraphQL Query
import { SELLER_PRODUCTS_QUERY } from "api/graphql/queries/product/GetProductList";
// Import fallback image
import fallbackImage from "assets/img/img-default/Empty state.jpg";
import { Column, PaginationState, VisibilityState } from "@tanstack/react-table";
// import { ProductsTableProductType, Status, ShippingMethod } from "./Products.types";

export type ProductsTableProductType = {
  id: string; // ✅ Unique identifier
  sku: string; // ✅ Product SKU
  product: string; // ✅ Product name
  productImage: string; // ✅ Primary product image
  type: string; // ✅ Product type
  sellerReference?: string;
  price: number; // ✅ Product price
  status: number; // ✅ Product Approval status
  product_status: number; // ✅ Product Catalog status
  visibility: number; // ✅ Visibility  
  weight: string | number; // ✅ Weight (can be numeric or string)
  stock: number; // ✅ Stock quantity
  isInStock: boolean; // ✅ Stock availability
  shippingMethod: number | string; // ✅ Shipping method
  fixCommission: number; // ✅ Fixed commission amount
  percentCommission: number; // ✅ Percentage commission
  specialPrice?: number; // ✅ Special discount price
  discountStartDate?: string | null; // ✅ Discount start date
  discountEndDate?: string | null; // ✅ Discount end date
  brand: string; // ✅ Product brand (default: Lenovo)
  dimensions: { // ✅ Product dimensions
    longueur: string | number;
    largeur: string | number;
    hauteur: string | number;
  };
  priceMin?: number;
  priceMax?: number;
  category: Array<{ id: string; name: string }>; // ✅ Categories as an array of objects
  variant?: number | string // ✅ Variants as an array of objects
  deactivation_reason?: { reason: string[]; description: string }; // Deactivation reasons
  // tags?: string[];
  // starred: boolean; // ✅ Favorite product flag
  // publishedOn: string; // ✅ Published date
};

export enum Status {
  All = "all",
  Approved = 1,
  Disapproved = 0,
  Rejected = 2
}
// Export ShippingMethod from CommissionDetailsModal
export { ShippingMethod } from "components/modals/CommissionDetailsModal";
const roundFixCommission = (value: number): number => {
  if (!value) return 0; // ✅ Ensure it always returns a number

  const thirdDigit = Math.floor((value * 1000) % 10); // ✅ Extract third decimal place
  const roundedValue = thirdDigit >= 5
    ? Math.ceil(value * 100) / 100
    : Math.floor(value * 100) / 100;

  return Number(roundedValue.toFixed(2)); // ✅ Ensure it's a number
};

const transformSellerProducts = (sellerProducts: any[]): ProductsTableProductType[] => {
  return sellerProducts.map((item, index) => {
    
    // Example logic for prices
    return {
      id: item.entityid || `unknown-${index}`,
      sku: item.sku || "N/A",
      sellerReference: item.seller_reference || "-" ,
      product: item.name || "Unnamed Product",
      productImage: item.image1?.url || fallbackImage,
      type: item.type_id || "N/A",
      price: item.prices || 0,
      status: item.status || "-",
      product_status: item.product_status || "-",
      visibility: item.visibility || "-",
      weight: item.weight ?? "-",
      stock: item.stock_data?.qty ?? 0, // ✅ Ensure stock exists
      isInStock: item.stock_data?.is_in_stock ?? false,
      shippingMethod: Number(item.mode_expedition) || "Unknown", // ✅ Convert to number
      percentCommission: Math.trunc(item.percent_commission_for_product || 0), // ✅ Prevent NaN
      fixCommission: roundFixCommission(item.commission_for_product || 0), // ✅ Apply rounding
      specialPrice: item.specialprice || null,
      discountStartDate: item.start_date || null,
      discountEndDate: item.end_date || null,
      brand: item.marque || "-", // Placeholder if no brand is provided
      dimensions: {
        longueur: Math.trunc(item.longueur25) || "-",
        largeur: Math.trunc(item.dimensions_de_l_outil_largeur1) || "-",
        hauteur: Math.trunc(item.dimensions_de_l_outil_hauteur1) || "-",
      },
      category: item.sellercategories,
      variant: item.variants_count || "-",
      deactivation_reason: item.deactivation_reason || [],
      starred: false,
      publishedOn: "-",
    };
  });
};

const Products = () => {
  const { t } = useTranslation();
  
  // Get location to check for filterByOutOfStock flag
  const location = useLocation();
  const filterByOutOfStock = location.state?.filterByOutOfStock || false;

  // Modal state for deactivation reason details
  const [showModal, setShowModal] = useState(false);
  const [currentReasonDetails, setCurrentReasonDetails] = useState<{ reason: string[]; description: string } | null>(null);
  
  // Modal state for commission details
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [currentCommissionDetails, setCurrentCommissionDetails] = useState<ProductsTableProductType | null>(null);

  // Handler for showing deactivation reason details in modal
  const handleShowReasonDetails = (details: { reason: string[]; description: string }) => {
    setCurrentReasonDetails(details);
    setShowModal(true);
  };

  // Handler for closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  // Handler for showing commission details in modal
  const handleShowCommissionDetails = (product: ProductsTableProductType) => {
    setCurrentCommissionDetails(product);
    setShowCommissionModal(true);
  };

  // Handler for closing the commission modal
  const handleCloseCommissionModal = () => {
    setShowCommissionModal(false);
  };
  
  // Calculate the total commission (works with both product types)
  const calculateTotalCommission = (product: ProductsTableProductType | ProductCommissionDetails): CommissionCalculation => {
    const fixCommission = product.fixCommission || 0;
    const percentCommission = (product.percentCommission || 0) / 100;
    
    // Determine if special price applies based on current date and special price date range
    const currentDate = new Date();
    const specialPriceStartDate = product.discountStartDate ? new Date(product.discountStartDate) : null;
    const specialPriceEndDate = product.discountEndDate ? new Date(product.discountEndDate) : null;
    
    // Need to safely check if specialPrice exists and is greater than 0
    const hasValidSpecialPrice = 
      product.specialPrice !== undefined && 
      product.specialPrice !== null && 
      Number(product.specialPrice) > 0;
    
    const isSpecialPriceApplicable = Boolean(
      specialPriceStartDate && 
      specialPriceEndDate && 
      currentDate >= specialPriceStartDate && 
      currentDate <= specialPriceEndDate && 
      hasValidSpecialPrice
    );
    
    // Use special price if applicable, otherwise use regular price
    const applicablePrice = isSpecialPriceApplicable 
      ? Number(product.specialPrice || 0)
      : Number(product.price || 0);
    
    const percentCommissionValue = applicablePrice * percentCommission;
    const totalCommission = fixCommission + percentCommissionValue;
    
    return {
      fixCommission,
      percentCommission: product.percentCommission || 0,
      percentCommissionValue: parseFloat(percentCommissionValue.toFixed(2)),
      applicablePrice,
      totalCommission: parseFloat(totalCommission.toFixed(2)),
      isSpecialPriceApplicable
    };
  };

  const [tableData, setTableData] = useState<ProductsTableProductType[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [paginationCount, setPaginationCount] = useState(
    {
      totalPages: 0,
      totalCount: 0
    }
  )
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState("");
  // State for active filter labels
  const [activeShippingMethodLabel, setActiveShippingMethodLabel] = useState<string>(t("products.shippingMethod"));
  const [activeSituationLabel, setActiveSituationLabel] = useState<string>(t("products.situation"));

  // State for active filters
  const [activeShippingMethodFilter, setShippingMethodFilter] = useState<number | "all">("all");
  const [activeStatusFilter, setActiveStatusFilter] = useState<number | "all">("all");
  // State for Approval Status Filter
  const [activeApprovalStatusFilter, setActiveApprovalStatusFilter] = useState<number | "all">("all");
  const [activeApprovalStatusLabel, setActiveApprovalStatusLabel] = useState<string>(t("products.approvalStatus"));
  // State for out of stock filter
  const [outOfStockFilter, setOutOfStockFilter] = useState<boolean>(filterByOutOfStock);

  // Function to build active filters for API calls
  const getActiveFilters = () => {
    const filters = {
      ...submittedSearchTerm && { name: submittedSearchTerm },
      ...activeShippingMethodFilter !== "all" && { mode_expedition: activeShippingMethodFilter },
      ...activeStatusFilter !== "all" && { status: activeStatusFilter },
      ...activeApprovalStatusFilter !== "all" && { product_status: activeApprovalStatusFilter },
    };

    // If filtering by out of stock products, set filter to find qty = 0
    if (outOfStockFilter) {
      return { ...filters, qty: 0 };
    }

    return filters;
  };

  const [fetchProducts, { loading, error, data }] = useLazyQuery(SELLER_PRODUCTS_QUERY, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // Initial fetch on component mount
  useEffect(() => {
    fetchProducts({
      variables: {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        filter: getActiveFilters(),
      },
    });
  }, []);

  // Handle initial out of stock filter from navigation
  useEffect(() => {
    if (filterByOutOfStock) {
      setOutOfStockFilter(true);
      
      // Reset to first page
      setPagination(prev => ({
        ...prev,
        pageIndex: 0
      }));
      
      // Fetch products with out of stock filter
      fetchProducts({
        variables: {
          page: 1,
          pageSize: pagination.pageSize,
          filter: getActiveFilters(),
        },
      });
    }
  }, [filterByOutOfStock]);
 
  // Pagination Handler
  const handlePageChange = (newPageIndex: number) => {
    // Calculate the safe page index (prevent out-of-bounds)
    const maxPageIndex = Math.max(0, paginationCount.totalPages - 1);
    const safePageIndex = Math.min(newPageIndex, maxPageIndex);
    
    // Update the pagination state
    setPagination((prev) => ({
      ...prev,
      pageIndex: safePageIndex,
    }));
    
    // Calculate the API page number (1-based)
    const apiPageNumber = safePageIndex + 1;
    
    // Fetch products with the new page
    fetchProducts({
      variables: {
        page: apiPageNumber,
        pageSize: pagination.pageSize,
        filter: getActiveFilters(),
      },
    });
  };
  // Shipping Method Filter Handler
  const setShippingMethodActiveFilter = (modeExpedition: number | null, label: string) => {
    // Update filter state
    setShippingMethodFilter(modeExpedition ?? "all");
    setActiveShippingMethodLabel(label);
    
    // Reset out of stock filter when applying shipping method filter
    setOutOfStockFilter(false);
    
    // Reset to first page when filter changes
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
    
    // Fetch with updated filter and reset to page 1
    fetchProducts({
      variables: {
        page: 1, // Always start at page 1 when filter changes
        pageSize: pagination.pageSize,
        filter: {
          ...getActiveFilters(),
          mode_expedition: modeExpedition,
        },
      },
    });
  };

  // Status Filter Handler
  const setStatusActiveFilter = (status: number | null, label: string) => {
    // Update filter state
    setActiveStatusFilter(status ?? "all");
    setActiveSituationLabel(label);
    
    // Reset out of stock filter when applying status filter
    setOutOfStockFilter(false);
    
    // Reset to first page when filter changes
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
    
    // Fetch with updated filter and reset to page 1
    fetchProducts({
      variables: {
        page: 1, // Always start at page 1 when filter changes
        pageSize: pagination.pageSize,
        filter: {
          ...getActiveFilters(),
          status: status,
        },
      },
    });
  };

  // Filter Handler for Approval Status
  const setApprovalStatusActiveFilter = (product_status: number | null, label: string) => {
    // Reset all filters when approval status filter is used
    setActiveApprovalStatusFilter(product_status ?? "all");
    setActiveApprovalStatusLabel(label);
    
    // Reset other filters
    setShippingMethodFilter("all");
    setActiveShippingMethodLabel("Shipping Method");
    setActiveStatusFilter("all");
    setActiveSituationLabel("Situation");
    setSearchTerm("");
    setSubmittedSearchTerm("");
    setOutOfStockFilter(false);
    
    // No direct fetchProducts call here - the useEffect will handle it
    // This prevents sending multiple queries
  };

  // Updated filterMenus with dynamic labels
  const filterMenus: FilterMenu[] = [
    {
      label: activeShippingMethodLabel, // Dynamic label based on selection
      items: [
        { label: t("shippingMethodType.esw"), onClick: () => setShippingMethodActiveFilter(ShippingMethod.ESW, t("shippingMethodType.esw")) },
        { label: t("shippingMethodType.ess"), onClick: () => setShippingMethodActiveFilter(ShippingMethod.ESS, t("shippingMethodType.ess")) },
        { label: t("shippingMethodType.fbw"), onClick: () => setShippingMethodActiveFilter(ShippingMethod.FBW, t("shippingMethodType.fbw")) },
        {
          label: t("all"), onClick: () => {
            setShippingMethodActiveFilter(null, t("products.shippingMethod")); // Reset filter and label
          }
        },
      ]
    },
    {
      label: activeSituationLabel, // Dynamic label based on selection
      items: [
        { label: t("onSale"), onClick: () => setStatusActiveFilter(1, t("onSale")) },
        { label: t("closeForSale"), onClick: () => setStatusActiveFilter(2, t("closeForSale")) },
        {
          label: t("all"), onClick: () => {
            setStatusActiveFilter(null, t("products.situation")); // Reset filter and label
          }
        },
      ]
    },
    {
      label: outOfStockFilter ? t("products.outOfStock") : t("products.stockStatus"),
      items: [
        { 
          label: t("products.outOfStock"), 
          onClick: () => {
            setOutOfStockFilter(true);
            // Reset other filters
            setShippingMethodFilter("all");
            setActiveShippingMethodLabel(t("products.shippingMethod"));
            setActiveStatusFilter("all");
            setActiveSituationLabel(t("products.situation"));
            setActiveApprovalStatusFilter("all");
            setActiveApprovalStatusLabel(t("products.approvalStatus"));
            setSearchTerm("");
            setSubmittedSearchTerm("");
            // Reset to first page
            setPagination(prev => ({
              ...prev,
              pageIndex: 0
            }));
          } 
        },
        {
          label: t("products.allStock"), onClick: () => {
            setOutOfStockFilter(false); // Reset filter
          }
        },
      ]
    }
  ];

  const approvedCount = data?.sellerProducts?.product_status?.approved_count || 0;
  const disapprovedCount = data?.sellerProducts?.product_status?.rejected_count || 0;
  const pendingCount = data?.sellerProducts?.product_status?.disapproved_count || 0;
  const totalCount = approvedCount + disapprovedCount + pendingCount;

  const tabItems: FilterTabItem[] = [
    { label: t("all"), value: "all", count: totalCount, onClick: () => setApprovalStatusActiveFilter(null, t("all")) },
    { label: t("approved"), value: "approved", count: approvedCount, onClick: () => setApprovalStatusActiveFilter(1, t("approved")) },
    { label: t("disapproved"), value: "pending", count: pendingCount, onClick: () => setApprovalStatusActiveFilter(2, t("disapproved")) },
    { label: t("rejected"), value: "rejected", count: disapprovedCount, onClick: () => setApprovalStatusActiveFilter(3, t("rejected")) },
  ];


  const others: any = {};
  useEffect(() => {
    if (data?.sellerProducts?.products.length > 0) {
      const transformed = transformSellerProducts(data.sellerProducts.products);
      setTableData(transformed);
  
      // ✅ Update pagination counts safely
      setPaginationCount((prev) => {
        const { total_count, total_pages } = data.sellerProducts.pagination;
  
        const safeTotalPages = Math.max(1, total_pages || 1); // ✅ Ensure totalPages is always at least 1
  
        if (prev.totalCount !== total_count || prev.totalPages !== total_pages) {
          return {
            totalCount: total_count ?? 0,
            totalPages: safeTotalPages,
          };
        }
        return prev;
      });
  
    } else {
      setTableData([]);
    }
  }, [data]);
  

  const table = useAdvanceTable({
    data: tableData,
    columns: productsTablecolumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    rowCount: paginationCount.totalCount ?? 0,
    manualPagination: true,
    onPaginationChange: (newPagination) => handlePageChange(newPagination.pageIndex),
    selection: true,
    initialState: { columnVisibility }, // Manage column visibility stat
    onColumnVisibilityChange: setColumnVisibility,
  });

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize }); // ✅ Always reset to first page
    table.setPageIndex(0);
  
    fetchProducts({
      variables: {
        page: 1,
        pageSize: pagination.pageSize,
        filter: getActiveFilters(),
      },
    });
  }, [activeShippingMethodFilter, activeStatusFilter, activeApprovalStatusFilter, submittedSearchTerm, outOfStockFilter]);
  
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value); // Update the searchTerm as the user types
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission
  
      // Store the search term for filter use
      setSubmittedSearchTerm(searchTerm);
  
      // Reset out of stock filter when searching
      setOutOfStockFilter(false);
      
      // Reset to first page when search is performed
      setPagination(prev => ({
        ...prev,
        pageIndex: 0
      }));
      
      // Update the table UI to show first page
      table.setPageIndex(0);
  
      // Fetch with search term and reset to page 1
      fetchProducts({
        variables: {
          page: 1, // Always start at page 1 when search changes
          pageSize: pagination.pageSize,
          filter: {
            ...getActiveFilters(),
            name: searchTerm || undefined, // Remove filter if empty
          },
        },
      });
    }
  };

  return (
    <div>
      {/* Modal for Deactivation Reason Details */}
      <Modal show={showModal} onHide={handleCloseModal} centered className="deactivation-reasons-modal">
        <Modal.Header closeButton className="bg-body-tertiary">
          <Modal.Title className="text-body">
            <FontAwesomeIcon icon={faFileExport} className="me-2" />
            {t('products.deactivationReasons', 'Deactivation Reasons')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {currentReasonDetails && currentReasonDetails.reason && currentReasonDetails.reason.length > 0 ? (
            <div>
              <div className="mb-4">
                <h5 className="text-body mb-3 border-bottom pb-2">
                  <strong>{t('products.reasonsForDeactivation', 'Reasons for Deactivation')}:</strong>
                </h5>
                <div className="mb-4">
                  {currentReasonDetails.reason.map((reason, index) => (
                    <div key={index} className="d-flex align-items-center mb-2">
                      <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-3" 
                        style={{ width: '28px', height: '28px', fontSize: '14px' }}>
                        {index + 1}
                      </div>
                      <div className="p-2 bg-body-tertiary rounded w-100">
                        <span className="fw-semibold">{reason}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {currentReasonDetails.description && (
                  <div className="mt-4">
                    <h5 className="text-body mb-3 border-bottom pb-2">
                      <strong>{t('products.additionalDetails', 'Additional Details')}:</strong>
                    </h5>
                    <div className="p-3 bg-body-tertiary rounded border">
                      <p className="mb-0 text-body">{currentReasonDetails.description}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-5">
              <div className="text-muted mb-3">
                <span className="fa-stack fa-2x">
                  <FontAwesomeIcon icon={faFileExport} className="text-secondary opacity-25 fa-stack-2x" />
                  <FontAwesomeIcon icon={faAngleDown} className="text-secondary fa-stack-1x" />
                </span>
              </div>
              <p className="fs-5 text-body">{t('products.noDeactivationReasons', 'No deactivation reasons available')}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-body-tertiary">
          <Button variant="outline-secondary" className="px-4" onClick={handleCloseModal}>
            {t('common.close', 'Close')}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Commission Details Modal */}
      <CommissionDetailsModal
        show={showCommissionModal}
        onHide={handleCloseCommissionModal}
        product={currentCommissionDetails ? {
          id: currentCommissionDetails.id,
          name: currentCommissionDetails.product,
          sku: currentCommissionDetails.sku,
          price: currentCommissionDetails.price,
          specialPrice: currentCommissionDetails.specialPrice,
          discountStartDate: currentCommissionDetails.discountStartDate,
          discountEndDate: currentCommissionDetails.discountEndDate,
          status: currentCommissionDetails.status,
          product_status: currentCommissionDetails.product_status,
          productStatus: currentCommissionDetails.product_status,
          productImage: currentCommissionDetails.productImage,
          shippingMethod: Number(currentCommissionDetails.shippingMethod), // Cast to number
          dimensions: currentCommissionDetails.dimensions,
          weight: currentCommissionDetails.weight,
          totalSold: currentCommissionDetails.stock, // using stock as totalSold
          fixCommission: currentCommissionDetails.fixCommission, // Add the fix commission value
          percentCommission: currentCommissionDetails.percentCommission // Add the percent commission value
        } : null}
        calculateCommission={calculateTotalCommission}
      />
      
      <div className="mb-9">
        <h2 className="mb-4">{t("products.title")}</h2>
        <FilterTab tabItems={tabItems} className="mb-2" />
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder={t("products.searchPlaceholder")}
                value={searchTerm} // Bind searchTerm to input
                onChange={handleSearchInputChange} // Update searchTerm on change
                onKeyPress={handleSearchKeyPress} // Handle search on Enter
              />
              <div className="scrollbar overflow-hidden-y">
                <FilterButtonGroup menus={filterMenus} />
              </div>

              {/* Column Visibility Dropdown */}
              <Dropdown>
                <Dropdown.Toggle
                  variant="outline-warning"
                  className="px-7 flex-shrink-0 dropdown-caret-none"
                  style={{
                    backgroundColor: 'transparent',
                    border: '2px solid #ff7f00',
                    color: '#ff7f00',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                  aria-label="Customize table columns"
                >
                  <FontAwesomeIcon icon={faColumns} className="me-2" />
                  {t("products.customizeTable")}
                  <FontAwesomeIcon
                    icon={faAngleDown}
                    className="ms-2"
                    transform="down-2"
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    padding: '12px',
                    borderRadius: '10px',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                    minWidth: '200px',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* <div className="mb-2 pb-2 border-bottom">
                    <span className="fw-bold text-muted">Toggle Columns</span>
                  </div> */}
                  {table.getAllColumns()
                    .filter(column => typeof column.columnDef.header === "string") // Only include named columns
                    .map((column) => (
                      <ColumnVisibilityToggle
                        key={column.id}
                        id={column.id}
                        label={column.columnDef.header?.toString() || ""}
                        checked={column.getIsVisible()}
                        onChange={(checked) => {
                          column.toggleVisibility(!!checked);
                        }}
                      />
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              <div className="ms-xxl-auto">
                <Button variant="link" className="text-body me-4 px-0">
                  <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                  {t("products.export")}
                </Button>
                <Link to="/products/add">
                  <Button variant="warning">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    {t("addProduct")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <Spinner animation="border" role="status" variant="warning">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <ProductsTablecolumns 
                totalCount={paginationCount.totalCount} 
                onShowReasonDetails={handleShowReasonDetails}
                onShowCommissionDetails={handleShowCommissionDetails}
              />
            )}
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Products;
